import { useState, useEffect } from 'react'
import { Tag } from '../components/Tag'
import { api, type ApiMember } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import styles from './PeopleScreen.module.css'

const filters = ['All', 'Members', 'Organisers', 'Volunteers', 'New faces']

export function PeopleScreen() {
  const [members, setMembers] = useState<ApiMember[]>([])
  const [activeFilter, setActiveFilter] = useState('All')
  const [loading, setLoading] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)
  const [followingIds, setFollowingIds] = useState<Set<number>>(new Set())
  const { user } = useAuth()

  useEffect(() => {
    Promise.all([
      api.members.list(),
      api.following.list().catch(() => [] as { following_id: number }[]),
    ]).then(([members, following]) => {
      setMembers(members)
      setFollowingIds(new Set(following.map(f => f.following_id)))
    })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false))
  }, [])

  const handleVouch = async (id: number) => {
    try {
      await api.members.vouch(id)
      setMembers(prev => prev.map(m => m.id === id ? { ...m, vouch_count: m.vouch_count + 1 } : m))
    } catch (err) {
      setActionError('Failed to vouch member. Please try again.')
    }
  }

  const handleFollow = async (id: number) => {
    try {
      await api.following.follow(id)
      setFollowingIds(prev => new Set(prev).add(id))
    } catch (err) {
      if (err instanceof Error && err.message === 'Already following') {
        setFollowingIds(prev => new Set(prev).add(id))
        return
      }
      setActionError('Failed to follow member. Please try again.')
    }
  }

  const filtered = activeFilter === 'All'
    ? members
    : members.filter(m => {
        if (activeFilter === 'Members') return m.role.toLowerCase() === 'member'
        if (activeFilter === 'Organisers') return m.role.toLowerCase() === 'organiser'
        if (activeFilter === 'Volunteers') return m.role.toLowerCase() === 'volunteer'
        if (activeFilter === 'New faces') return m.status.toLowerCase() === 'new'
        return true
      })

  const getVouchInitials = (vouchCount: number) => {
    if (vouchCount === 0) return '—'
    if (vouchCount === 1) return '1 vouch'
    return `${vouchCount} vouches`
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Directory</h2>
        <p className={styles.headerSubtitle}>
          Everyone here was vouched in by someone — shown as initials. Full names are visible to organisers only.
        </p>

        <div className={styles.filterContainer}>
          {filters.map(f => (
            <span
              key={f}
              onClick={() => setActiveFilter(f)}
              className={styles.filterChip}
            >
              <Tag variant={activeFilter === f ? 'accent' : 'neutral'}>{f}</Tag>
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.noMembers}>
          No members found.
        </div>
      ) : (
        <>
          {actionError && (
            <div className={styles.errorBanner}>
              <span>{actionError}</span>
              <button onClick={() => setActionError(null)} className={styles.errorBannerButton}>×</button>
            </div>
          )}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Name</th>
                  <th className={styles.tableHeader}>Role</th>
                  <th className={styles.tableHeader}>Vouched by</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id}>
                    <td className={styles.tableCell}>
                      <div className={styles.tableCellName}>{m.display_name}</div>
                      <Tag variant={m.status === 'Member' || m.status === 'member' ? 'accent' : 'neutral'} style={{ marginTop: '4px' }}>
                        {m.status}
                      </Tag>
                    </td>
                    <td className={styles.tableCell + ' ' + styles.tableCellRole}>{m.role}</td>
                    <td className={styles.tableCell + ' ' + styles.tableCellVouched}>
                      {getVouchInitials(m.vouch_count)}
                      {user && m.id !== user.id && (
                        <>
                          <button
                            onClick={() => handleVouch(m.id)}
                            className={styles.vouchButton}
                          >
                            Vouch
                          </button>
                          {followingIds.has(m.id) ? (
                            <button
                              disabled
                              className={styles.followButton}
                            >
                              Following
                            </button>
                          ) : (
                            <button
                              onClick={() => handleFollow(m.id)}
                              className={styles.followButton}
                            >
                              Follow
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
